/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Input,
  Spin,
  Tag,
  message,
  Tooltip,
  Badge,
  Divider,
  Space,
  Card,
  Typography,
  Row,
  Col,
  Collapse,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  SaveOutlined,
  LockOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  UserOutlined,
  FileTextOutlined,
  BarChartOutlined,
  TeamOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { permissionsApi, rolesApi } from "../../apis";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const ROLE_NAME_REGEX = /^[a-z0-9._-]+$/;
const { TextArea } = Input;

// Helper function to get icon based on permission category
const getCategoryIcon = (category) => {
  const iconMap = {
    'user': <UserOutlined />,
    'users': <TeamOutlined />,
    'role': <SettingOutlined />,
    'roles': <SettingOutlined />,
    'permission': <LockOutlined />,
    'permissions': <LockOutlined />,
    'report': <BarChartOutlined />,
    'reports': <BarChartOutlined />,
    'document': <FileTextOutlined />,
    'documents': <FileTextOutlined />,
    'dashboard': <AppstoreOutlined />,
    'general': <AppstoreOutlined />,
  };
  
  return iconMap[category.toLowerCase()] || <SettingOutlined />;
};

// Human-readable permission labels
const getPermissionLabel = (permissionName) => {
  const parts = permissionName.split('.');
  const action = parts[parts.length - 1];
  const resource = parts.slice(0, -1).join(' ');
  
  const actionLabels = {
    'create': 'Create',
    'view': 'View',
    'edit': 'Edit',
    'update': 'Update',
    'delete': 'Delete',
    'list': 'List',
    'read': 'Read',
    'write': 'Write',
    'manage': 'Manage',
    'export': 'Export',
    'import': 'Import',
    'approve': 'Approve',
    'reject': 'Reject',
    'assign': 'Assign',
  };
  
  const formattedAction = actionLabels[action] || action.charAt(0).toUpperCase() + action.slice(1);
  const formattedResource = resource.split('.').map(r => 
    r.charAt(0).toUpperCase() + r.slice(1)
  ).join(' ');
  
  return `${formattedAction} ${formattedResource}`.trim();
};

// Get permission description
const getPermissionDescription = (permissionName) => {
  const parts = permissionName.split('.');
  const action = parts[parts.length - 1];
  const resource = parts.slice(0, -1).join(' ');
  
  const descriptions = {
    'create': `Create new ${resource}`,
    'view': `View ${resource} details`,
    'edit': `Edit existing ${resource}`,
    'update': `Update ${resource} information`,
    'delete': `Delete ${resource}`,
    'list': `List all ${resource}`,
    'read': `Read ${resource} data`,
    'write': `Write to ${resource}`,
    'manage': `Manage all aspects of ${resource}`,
    'export': `Export ${resource} data`,
    'import': `Import ${resource} data`,
    'approve': `Approve ${resource} requests`,
    'reject': `Reject ${resource} requests`,
    'assign': `Assign ${resource}`,
  };
  
  return descriptions[action] || `Permission to ${action} ${resource}`;
};

const titleCase = (value = "") =>
  value
    .split(/[_\-.]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const groupPermissions = (permissions = []) => {
  const groups = permissions.reduce((acc, permission) => {
    const name = permission?.name || "";
    const key = name.includes(".") ? name.split(".")[0] : "general";
    if (!acc[key]) acc[key] = [];
    acc[key].push(permission);
    return acc;
  }, {});

  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, items]) => ({
      key,
      label: titleCase(key),
      items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
    }));
};

const normalizeMessage = (error, fallback) => {
  const msg = error?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join("\n");
  return msg || fallback;
};

const RoleForm = ({ mode = "create", roleId = null }) => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    permissionIds: [],
  });
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const isEdit = mode === "edit";
  const isSystemRole = role?.isSystem === true;

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        const [permissionList, existingRole] = await Promise.all([
          permissionsApi.getAll(),
          isEdit && roleId ? rolesApi.getById(roleId) : Promise.resolve(null),
        ]);

        if (!active) return;

        setPermissions(permissionList || []);

        if (existingRole) {
          setRole(existingRole);
          setForm({
            name: existingRole.name || "",
            description: existingRole.description || "",
            permissionIds: (existingRole.permissions || []).map(
              (permission) => permission.id,
            ),
          });
        }
      } catch (error) {
        if (!active) return;
        message.error(
          normalizeMessage(error, "Failed to load role form data"),
        );
        history.push("/roles");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [history, isEdit, roleId]);

  const groupedPermissions = useMemo(
    () => groupPermissions(permissions),
    [permissions],
  );

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return groupedPermissions;
    
    return groupedPermissions
      .map(group => ({
        ...group,
        items: group.items.filter(permission =>
          permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          getPermissionLabel(permission.name).toLowerCase().includes(searchTerm.toLowerCase())
        )
      }))
      .filter(group => group.items.length > 0);
  }, [groupedPermissions, searchTerm]);

  const selectedPermissionIds = useMemo(
    () => new Set(form.permissionIds),
    [form.permissionIds],
  );

  const validate = () => {
    const nextErrors = {};
    const trimmedName = form.name.trim().toLowerCase();

    if (!trimmedName) {
      nextErrors.name = "Role name is required";
    } else if (!ROLE_NAME_REGEX.test(trimmedName)) {
      nextErrors.name =
        "Use lowercase letters, numbers, dots, underscores, or hyphens only";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const togglePermission = (permissionId, checked) => {
    if (isSystemRole) return;

    setForm((current) => {
      const next = new Set(current.permissionIds);
      if (checked) next.add(permissionId);
      else next.delete(permissionId);
      return { ...current, permissionIds: Array.from(next) };
    });
  };

  const toggleGroup = (group, checked) => {
    if (isSystemRole) return;

    setForm((current) => {
      const next = new Set(current.permissionIds);
      group.items.forEach((permission) => {
        if (checked) next.add(permission.id);
        else next.delete(permission.id);
      });
      return { ...current, permissionIds: Array.from(next) };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);
      const payload = {
        name: form.name.trim().toLowerCase(),
        description: form.description.trim() || undefined,
        permissionIds: form.permissionIds,
      };

      if (isEdit) {
        await rolesApi.update(roleId, payload);
        message.success("Role updated successfully");
      } else {
        await rolesApi.create(payload);
        message.success("Role created successfully");
      }

      history.push("/roles");
    } catch (error) {
      message.error(
        normalizeMessage(
          error,
          isEdit ? "Failed to update role" : "Failed to create role",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="main-wrapper">
        <Header onMenuClick={() => {}} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <Spin size="large" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Card className="mb-0" bordered={false}>
            <div className="page-header" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Title level={4} style={{ marginBottom: 8 }}>
                    {isEdit ? "Edit Role" : "Create New Role"}
                  </Title>
                  <Text type="secondary">
                    {isEdit 
                      ? "Update role details and manage permissions" 
                      : "Define a new role and set its permissions"}
                  </Text>
                </div>
                <Button
                  onClick={() => history.push("/roles")}
                  icon={<ArrowLeftOutlined />}
                >
                  Back to Roles
                </Button>
              </div>
            </div>

            {isSystemRole && (
              <Alert
                type="info"
                showIcon
                icon={<LockOutlined />}
                className="mb-4"
                message="System Role"
                description="This is a system role. While you can update its description, the role name and assigned permissions are locked for security reasons."
              />
            )}

            <form onSubmit={handleSubmit}>
              <Card 
                title={
                  <Space>
                    <UserOutlined />
                    <span>Role Information</span>
                  </Space>
                }
                className="mb-4"
                size="small"
              >
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <div className="mb-3">
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                        Role Name <span className="text-danger">*</span>
                        <Tooltip title="Use lowercase letters, numbers, dots, underscores, or hyphens">
                          <InfoCircleOutlined style={{ marginLeft: 8, color: '#8c8c8c' }} />
                        </Tooltip>
                      </label>
                      <Input
                        value={form.name}
                        onChange={(e) => updateField("name", e.target.value)}
                        placeholder="e.g., reservations_manager"
                        disabled={saving || isSystemRole}
                        status={errors.name ? "error" : ""}
                        size="large"
                      />
                      {errors.name && (
                        <Text type="danger" style={{ fontSize: 12, marginTop: 4 }}>
                          {errors.name}
                        </Text>
                      )}
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Lowercase only, no spaces. Example: reservations_manager
                      </Text>
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <div className="mb-3">
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                        Description
                      </label>
                      <TextArea
                        value={form.description}
                        onChange={(e) => updateField("description", e.target.value)}
                        rows={4}
                        placeholder="Describe what this role is responsible for..."
                        disabled={saving}
                        style={{ resize: 'vertical' }}
                      />
                    </div>
                  </Col>
                </Row>
              </Card>

              <Card 
                title={
                  <Space>
                    <LockOutlined />
                    <span>Permissions</span>
                    <Badge 
                      count={form.permissionIds.length} 
                      showZero 
                      style={{ backgroundColor: '#7B4D12' }}
                    />
                  </Space>
                }
                extra={
                  <Input.Search
                    placeholder="Search permissions..."
                    allowClear
                    style={{ width: 250 }}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                }
                className="mb-4"
              >
                {!filteredGroups.length ? (
                  <Empty 
                    description={searchTerm ? "No permissions match your search" : "No permissions found"}
                  />
                ) : (
                  <Collapse 
                    defaultActiveKey={filteredGroups.map(g => g.key)}
                    ghost
                  >
                    {filteredGroups.map((group) => {
                      const groupIds = group.items.map((item) => item.id);
                      const checkedCount = groupIds.filter((id) =>
                        selectedPermissionIds.has(id),
                      ).length;
                      const allChecked =
                        groupIds.length > 0 && checkedCount === groupIds.length;
                      const indeterminate =
                        checkedCount > 0 && checkedCount < groupIds.length;

                      return (
                        <Panel
                          key={group.key}
                          header={
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <Space>
                                {getCategoryIcon(group.key)}
                                <strong style={{ fontSize: 16 }}>{group.label}</strong>
                                <Tag color="default" style={{ marginLeft: 8 }}>
                                  {group.items.length} permissions
                                </Tag>
                              </Space>
                              <Checkbox
                                checked={allChecked}
                                indeterminate={indeterminate}
                                onChange={(e) => toggleGroup(group, e.target.checked)}
                                disabled={saving || isSystemRole}
                                onClick={(e) => e.stopPropagation()}
                              >
                                Select All ({checkedCount}/{group.items.length})
                              </Checkbox>
                            </div>
                          }
                        >
                          <Row gutter={[16, 16]}>
                            {group.items.map((permission) => (
                              <Col xs={24} md={12} key={permission.id}>
                                <div
                                  onClick={() => !isSystemRole && !saving && togglePermission(permission.id, !selectedPermissionIds.has(permission.id))}
                                  style={{
                                    padding: '12px 16px',
                                    borderRadius: 8,
                                    background: selectedPermissionIds.has(permission.id)
                                      ? 'linear-gradient(135deg, #fff7ed 0%, #fffbf5 100%)'
                                      : '#fafafa',
                                    border: `1px solid ${selectedPermissionIds.has(permission.id) ? '#7B4D12' : '#f0f0f0'}`,
                                    cursor: isSystemRole || saving ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                    opacity: isSystemRole ? 0.6 : 1,
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                    <Checkbox
                                      checked={selectedPermissionIds.has(permission.id)}
                                      onChange={(e) => togglePermission(permission.id, e.target.checked)}
                                      disabled={saving || isSystemRole}
                                    />
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: 600, marginBottom: 4, color: '#1a1d2e' }}>
                                        {getPermissionLabel(permission.name)}
                                      </div>
                                      <Text type="secondary" style={{ fontSize: 12 }}>
                                        {getPermissionDescription(permission.name)}
                                      </Text>
                                      <div style={{ marginTop: 4 }}>
                                        <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                                          {permission.name}
                                        </Text>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </Panel>
                      );
                    })}
                  </Collapse>
                )}
              </Card>

              <div style={{ textAlign: 'right', paddingTop: 16 }}>
                <Space>
                  <Button
                    onClick={() => history.push("/roles")}
                    disabled={saving}
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={saving}
                    icon={<SaveOutlined />}
                    size="large"
                    style={{
                      backgroundColor: "#7B4D12",
                      borderColor: "#7B4D12",
                    }}
                  >
                    {isEdit ? "Save Changes" : "Create Role"}
                  </Button>
                </Space>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RoleForm;